import * as _ from 'underscore';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { IUserNotification } from '@tableau/user-notifications-client-core';
import { GetUserNotifications, UpdateUserNotificationsStatus, DeleteUserNotification, CheckHasUnseenUserNotifications, UpdateLastSeenUserNotification } from '../../../app/actions/AppActionTypes';
import { GetUserNotificationsAction, getUserNotificationsSucceededAction, getUserNotificationsFailedAction } from '../actions/GetUserNotificationsAction';
import { UpdateUserNotificationsStatusAction, updateUserNotificationsStatusSucceededAction } from '../actions/UpdateUserNotificationStatusAction';
import { requestFailed } from '../../../app/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';
import { DeleteUserNotificationAction, deleteUserNotificationSucceededAction } from '../actions/DeleteUserNotification';
import { CheckHasUnseenUserNotificationsAction, checkHasUnseenUserNotificationsSucceededAction } from '../actions/CheckHasUnseenUserNotificationsAction';
import { UserNotificationsResources } from '../../resources/UserNotificationsResource';
import { updateLastSeenUserNotificationAction, updateLastSeenUserNotificationSucceededAction, UpdateLastSeenUserNotificationAction } from '../actions/UpdateLastSeenUserNotificationAction';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';

export function* handleGetUserNotificationRequest(action: GetUserNotificationsAction) {
  try {
    const notifications: _.Dictionary<IUserNotification<any>> = yield call(getUserNotificationsRequest);
    yield put(getUserNotificationsSucceededAction(notifications));

    if (!_.isEmpty(notifications)) {
      const lastSeenId = _.max(_.values(notifications), n => n.creationTime).id;
      yield put(updateLastSeenUserNotificationAction(lastSeenId));
    }
  } catch (e) {
    yield put(getUserNotificationsFailedAction());
  }
}

export function* handleUpdateUserNotificationsStatusRequest(action: UpdateUserNotificationsStatusAction) {
  try {
    yield call(updateUserNotificationsStatusRequest, action.notificationIds, action.isRead);
    yield put(updateUserNotificationsStatusSucceededAction(action.notificationIds, action.isRead));
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleDeleteUserNotificationRequest(action: DeleteUserNotificationAction) {
  try {
    yield call(deleteUserNotificationRequest, action.notificationId);
    yield put(deleteUserNotificationSucceededAction(action.notificationId));
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleCheckHasUnseenUserNotificationsRequest(action: CheckHasUnseenUserNotificationsAction) {
  try {
    const hasUnseen: boolean = yield call(checkHasUnseenUserNotificationsRequest);
    yield put(checkHasUnseenUserNotificationsSucceededAction(hasUnseen));
  } catch (e) {
    if (e === CommonErrorCode.InvalidSession) {
      yield put(requestFailed(e, Messages.errorUnknown()));
    }
  }
}

export function* handleUpdateLastSeenUserNotificationRequest(action: UpdateLastSeenUserNotificationAction) {
  try {
    yield call(updateLastSeenUserNotificationRequest, action.lastSeenUserNotificationId); // TODO: finalize param
    yield put(updateLastSeenUserNotificationSucceededAction());
  } catch (ignored) {
  }
}

export function getUserNotificationsRequest(): Promise<_.Dictionary<IUserNotification<any>>> {
  return Promise.resolve(UserNotificationsResources.getAllUserNotifications());
}

export function updateUserNotificationsStatusRequest(ids: string[], isRead: boolean): Promise<{}> {
  const batchedIds = _.chunk(ids, 1000);
  return Promise.all(batchedIds.map((batch: string[]) => UserNotificationsResources.updateNotificationsStatus(batch, isRead)));
}

export function deleteUserNotificationRequest(id: string): Promise<{}> {
  return UserNotificationsResources.deleteNotification(id);
}

export function checkHasUnseenUserNotificationsRequest(): Promise<boolean> {
  return UserNotificationsResources.checkHasUnseenUserNotifications();
}

export function updateLastSeenUserNotificationRequest(id: string) {
  return UserNotificationsResources.updateLastSeenUserNotification(id);
}

export function* rootUserNotificationSagas(): SagaIterator {
  yield takeLatest(GetUserNotifications, handleGetUserNotificationRequest);
  yield takeEvery(UpdateUserNotificationsStatus, handleUpdateUserNotificationsStatusRequest);
  yield takeEvery(DeleteUserNotification, handleDeleteUserNotificationRequest);
  yield takeLatest(CheckHasUnseenUserNotifications, handleCheckHasUnseenUserNotificationsRequest);
  yield takeLatest(UpdateLastSeenUserNotification, handleUpdateLastSeenUserNotificationRequest);
}